import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { EventTask, updateTask } from '../api/event-tasks';
import { useAuth } from '../contexts/AuthContext';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

/* ─── Types ─── */
interface EventGroup {
  eventId: string | null;
  eventName: string;
  eventDate: string | null;
  eventStartTime: string | null;
  tasks: EventTask[];
  doneCount: number;
}

type ViewMode = 'timeline' | 'assignee' | 'table';
type StatusFilter = '' | 'ToDo' | 'InProgress' | 'Done' | 'overdue';

/* ─── Helpers ─── */
function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function isOverdue(task: EventTask): boolean {
  if (task.status === 'Done' || task.status === 'Cancelled') return false;
  const due = new Date(task.dueDate);
  const now = new Date();
  return due < now;
}

function getInitials(assignee: { firstName: string; lastName: string } | null): string {
  if (!assignee) return '??';
  return `${assignee.firstName[0] || ''}${assignee.lastName[0] || ''}`.toUpperCase();
}

function groupByEvent(tasks: EventTask[]): EventGroup[] {
  const map = new Map<string, EventGroup>();

  for (const task of tasks) {
    const key = task.event?.id || '__general__';
    if (!map.has(key)) {
      map.set(key, {
        eventId: task.event?.id || null,
        eventName: task.event?.name || 'General Tasks',
        eventDate: (task.event as any)?.date || null,
        eventStartTime: (task.event as any)?.startTime || null,
        tasks: [],
        doneCount: 0,
      });
    }
    const group = map.get(key)!;
    group.tasks.push(task);
    if (task.status === 'Done') group.doneCount++;
  }

  // Sort groups by event date (null goes last)
  return Array.from(map.values()).sort((a, b) => {
    if (!a.eventDate && !b.eventDate) return 0;
    if (!a.eventDate) return 1;
    if (!b.eventDate) return -1;
    return a.eventDate.localeCompare(b.eventDate);
  });
}

/* ─── Progress Ring SVG ─── */
function ProgressRing({ percent, size = 64 }: { percent: number; size?: number }) {
  const r = (size - 8) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={6}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="#a7f3d0" strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <span style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        fontSize: size < 50 ? '0.7rem' : '0.95rem',
        fontWeight: 700, color: '#fff',
      }}>
        {percent}%
      </span>
    </div>
  );
}

/* ─── Styles ─── */
const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1100px', margin: '0 auto' },
  pageMobile: { padding: '1rem', maxWidth: '100%' },
  header: { marginBottom: '1.5rem' },
  title: { fontFamily: FONT_HEADING, fontSize: '1.6rem', color: PRIMARY, marginBottom: '0.25rem' },
  subtitle: { fontSize: '0.88rem', color: TEXT_MUTED },
  progressBanner: {
    display: 'flex', alignItems: 'center', gap: '1.25rem',
    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d8f5e 100%)`,
    borderRadius: '12px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', color: '#fff',
  },
  progressBannerMobile: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d8f5e 100%)`,
    borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1rem', color: '#fff',
  },
  headline: { fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.3rem' },
  breakdown: { display: 'flex', gap: '1rem', fontSize: '0.78rem', opacity: 0.9 },
  filterBar: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' as const },
  filterBarMobile: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', overflowX: 'auto' as const, flexWrap: 'nowrap' as const, paddingBottom: '0.5rem' },
  filterPill: {
    padding: '0.4rem 0.85rem', borderRadius: '20px', border: `1px solid ${BORDER_LIGHT}`,
    background: BG_CARD, fontSize: '0.78rem', cursor: 'pointer', color: TEXT_MUTED,
    transition: 'all 0.15s', whiteSpace: 'nowrap' as const, flexShrink: 0,
  },
  filterPillActive: {
    padding: '0.4rem 0.85rem', borderRadius: '20px', border: `1px solid ${PRIMARY}`,
    background: PRIMARY, fontSize: '0.78rem', cursor: 'pointer', color: '#fff',
    whiteSpace: 'nowrap' as const, flexShrink: 0,
  },
  viewToggle: { display: 'flex', gap: '0.25rem', background: '#f3f4f6', borderRadius: '8px', padding: '3px', marginBottom: '1rem' },
  viewBtn: { padding: '0.4rem 0.75rem', border: 'none', borderRadius: '6px', background: 'transparent', fontSize: '0.78rem', cursor: 'pointer', color: TEXT_MUTED, transition: 'all 0.15s' },
  viewBtnActive: { padding: '0.4rem 0.75rem', border: 'none', borderRadius: '6px', background: '#fff', fontSize: '0.78rem', cursor: 'pointer', color: PRIMARY, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  timeline: { position: 'relative' as const, paddingLeft: '1.5rem' },
  timelineLine: { content: '', position: 'absolute' as const, left: '8px', top: 0, bottom: 0, width: '2px', background: `linear-gradient(to bottom, ${PRIMARY}, #a7f3d0, transparent)`, borderRadius: '2px' },
  eventGroup: { marginBottom: '1.75rem', position: 'relative' as const },
  eventDot: { position: 'absolute' as const, left: '-1.5rem', top: '0.6rem', width: '14px', height: '14px', borderRadius: '50%', background: PRIMARY, border: `3px solid ${BG_PAGE}`, boxShadow: `0 0 0 2px ${PRIMARY}`, zIndex: 1 },
  eventDotComplete: { position: 'absolute' as const, left: '-1.5rem', top: '0.6rem', width: '14px', height: '14px', borderRadius: '50%', background: '#a7f3d0', border: `3px solid ${BG_PAGE}`, boxShadow: '0 0 0 2px #2d8f5e', zIndex: 1 },
  eventHeader: { display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.6rem', flexWrap: 'wrap' as const },
  eventName: { fontWeight: 700, fontSize: '1rem', color: TEXT_DARK },
  eventDate: { fontSize: '0.75rem', color: TEXT_MUTED },
  eventProgress: { fontSize: '0.72rem', color: TEXT_MUTED, marginLeft: 'auto', background: '#f0f7f2', padding: '0.2rem 0.5rem', borderRadius: '10px' },
  taskCard: {
    background: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px',
    padding: '0.85rem 1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start',
    gap: '0.75rem', transition: 'all 0.15s', cursor: 'pointer',
  },
  taskTitle: { fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.2rem' },
  taskMeta: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap' as const, fontSize: '0.72rem', color: TEXT_MUTED },
  avatar: {
    width: '26px', height: '26px', borderRadius: '50%', background: '#e8f5e9',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.65rem', fontWeight: 700, color: PRIMARY, flexShrink: 0, marginTop: '2px',
  },
  checkbox: {
    width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #d1d5db',
    flexShrink: 0, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.65rem', transition: 'all 0.15s', cursor: 'pointer',
  },
  quickActions: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' as const },
  qaBtn: {
    display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem',
    borderRadius: '8px', border: `1px solid ${BORDER_LIGHT}`, background: BG_CARD,
    fontSize: '0.78rem', cursor: 'pointer', color: TEXT_MUTED, transition: 'all 0.15s',
  },
  qaBtnPrimary: {
    display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem',
    borderRadius: '8px', border: `1px solid ${PRIMARY}`, background: PRIMARY,
    fontSize: '0.78rem', cursor: 'pointer', color: '#fff', transition: 'all 0.15s',
  },
};

/* ─── Main Component ─── */
export function WeekendTasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<EventTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [showMyOnly, setShowMyOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<EventTask[]>('/event-tasks?category=Weekend');
      setTasks(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  /* ─── Computed ─── */
  const filteredTasks = tasks.filter((t) => {
    if (statusFilter === 'overdue') return isOverdue(t);
    if (statusFilter && t.status !== statusFilter) return false;
    if (showMyOnly && user) {
      const userId = user.sub;
      const primaryMatch = t.assignee?.userId === userId;
      const secondaryMatch = t.assignees?.some(
        (a) => a.member?.userId === userId
      );
      if (!primaryMatch && !secondaryMatch) return false;
    }
    return true;
  });

  const totalTasks = tasks.length;
  const doneTasks = tasks.filter((t) => t.status === 'Done').length;
  const inProgressTasks = tasks.filter((t) => t.status === 'InProgress').length;
  const todoTasks = tasks.filter((t) => t.status === 'ToDo').length;
  const overdueTasks = tasks.filter(isOverdue).length;
  const percent = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  const eventGroups = groupByEvent(filteredTasks);

  /* ─── Status cycling ─── */
  const cycleStatus = async (task: EventTask) => {
    const nextStatus: Record<string, string> = {
      ToDo: 'InProgress',
      InProgress: 'Done',
      Done: 'ToDo',
    };
    const newStatus = nextStatus[task.status] || 'ToDo';
    try {
      await updateTask(task.id, { status: newStatus });
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: newStatus as any } : t))
      );
    } catch {
      // Silently fail — user can retry
    }
  };

  const toggleGroupExpand = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ─── Print handler ─── */
  const handlePrint = () => {
    const headerCells = '<th>Task</th><th>Event</th><th>Priority</th><th>Due</th><th>Assignee</th><th>Status</th>';
    const rows = tasks.map((t) => {
      const assigneeName = t.assignee ? `${t.assignee.firstName} ${t.assignee.lastName}` : 'Unassigned';
      const dueDate = t.dueDate ? formatDate(t.dueDate) : '';
      return `<tr><td>${t.title}</td><td>${t.event?.name || 'General'}</td><td>${t.priority}</td><td>${dueDate}</td><td>${assigneeName}</td><td>${t.status}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head>
      <title>Weekend Tasks Report</title>
      <style>
        body { font-family: sans-serif; padding: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 8px 10px; border-bottom: 2px solid #333; font-size: 13px; }
        td { padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 12px; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        p { color: #666; font-size: 12px; margin-bottom: 16px; }
      </style>
    </head><body>
      <h1>Weekend Tasks Report</h1>
      <p>${totalTasks} tasks (${doneTasks} complete, ${overdueTasks} overdue) • Printed ${new Date().toLocaleDateString()}</p>
      <table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  /* ─── Send as notification ─── */
  const handleSendNotification = async () => {
    const summary = eventGroups.map((g) =>
      `${g.eventName}: ${g.doneCount}/${g.tasks.length} complete`
    ).join('\n');
    const body = `Weekend Tasks Progress:\n\n${summary}\n\nOverall: ${percent}% complete (${doneTasks}/${totalTasks})${overdueTasks > 0 ? `\n⚠️ ${overdueTasks} overdue` : ''}`;
    try {
      const announcement = await apiClient<{ id: string }>('/announcements', {
        method: 'POST',
        body: { title: '📋 Weekend Tasks Update', body, audience: 'DelegatesOnly' },
      });
      await apiClient(`/announcements/${announcement.id}/publish`, { method: 'POST' });
      alert('Task summary sent as notification');
    } catch {
      alert('Failed to send notification');
    }
  };

  /* ─── Render: Task Card ─── */
  const renderTaskCard = (task: EventTask) => {
    const overdue = isOverdue(task);
    const borderLeft = task.status === 'Done' ? '3px solid #a7f3d0'
      : task.status === 'InProgress' ? '3px solid #fbbf24'
      : overdue ? '3px solid #ef4444' : undefined;

    const checkboxStyle: React.CSSProperties = {
      ...styles.checkbox,
      ...(task.status === 'Done' ? { background: PRIMARY, borderColor: PRIMARY, color: '#fff' } : {}),
      ...(task.status === 'InProgress' ? { borderColor: '#fbbf24', background: '#fef3c7' } : {}),
    };

    const titleStyle: React.CSSProperties = {
      ...styles.taskTitle,
      ...(task.status === 'Done' ? { textDecoration: 'line-through', opacity: 0.6 } : {}),
    };

    return (
      <div
        key={task.id}
        style={{ ...styles.taskCard, borderLeft, ...(task.status === 'Done' ? { opacity: 0.6 } : {}) }}
      >
        <div
          style={checkboxStyle}
          onClick={(e) => { e.stopPropagation(); cycleStatus(task); }}
          role="button"
          aria-label={`Change status of ${task.title}`}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') cycleStatus(task); }}
        >
          {task.status === 'Done' && '✓'}
          {task.status === 'InProgress' && '⟳'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>{task.title}</div>
          <div style={styles.taskMeta}>
            <span>{task.subcommittee?.name || ''}</span>
            <span style={
              task.priority === 'High' ? { color: '#dc2626', fontWeight: 600 }
              : task.priority === 'Medium' ? { color: '#d97706' }
              : { color: '#059669' }
            }>
              ● {task.priority}
            </span>
            {overdue && <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠️ Overdue</span>}
            {!overdue && task.dueDate && <span>Due: {formatDate(task.dueDate)}</span>}
            {isMobile && task.assignee && (
              <span>👤 {task.assignee.firstName} {task.assignee.lastName[0]}.</span>
            )}
          </div>
        </div>
        {!isMobile && (
          <div style={styles.avatar} title={task.assignee ? `${task.assignee.firstName} ${task.assignee.lastName}` : 'Unassigned'}>
            {getInitials(task.assignee)}
          </div>
        )}
      </div>
    );
  };

  /* ─── Render: Timeline View ─── */
  const renderTimeline = () => (
    <div style={styles.timeline}>
      <div style={styles.timelineLine} />
      {eventGroups.map((group) => {
        const allDone = group.doneCount === group.tasks.length;
        const groupKey = group.eventId || '__general__';
        const doneTasks = group.tasks.filter((t) => t.status === 'Done');
        const activeTasks = group.tasks.filter((t) => t.status !== 'Done');
        const showDone = expandedGroups.has(groupKey);

        return (
          <div key={groupKey} style={styles.eventGroup}>
            <div style={allDone ? styles.eventDotComplete : styles.eventDot} />
            <div style={styles.eventHeader}>
              <span style={styles.eventName}>{group.eventName}</span>
              {group.eventDate && (
                <span style={styles.eventDate}>
                  {formatDate(group.eventDate)}
                  {group.eventStartTime && ` • ${formatTime(group.eventStartTime)}`}
                </span>
              )}
              <span style={styles.eventProgress}>
                {group.doneCount}/{group.tasks.length}{allDone ? ' ✓' : ''}
              </span>
            </div>

            {activeTasks.map(renderTaskCard)}

            {doneTasks.length > 0 && !showDone && (
              <button
                onClick={() => toggleGroupExpand(groupKey)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', width: '100%',
                  textAlign: 'center', padding: '0.4rem', fontSize: '0.72rem', color: TEXT_MUTED,
                }}
              >
                + {doneTasks.length} completed task{doneTasks.length > 1 ? 's' : ''}
              </button>
            )}
            {showDone && doneTasks.map(renderTaskCard)}
            {showDone && doneTasks.length > 0 && (
              <button
                onClick={() => toggleGroupExpand(groupKey)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', width: '100%',
                  textAlign: 'center', padding: '0.4rem', fontSize: '0.72rem', color: TEXT_MUTED,
                }}
              >
                Hide completed
              </button>
            )}
          </div>
        );
      })}
      {eventGroups.length === 0 && !loading && (
        <p style={{ color: TEXT_MUTED, fontSize: '0.88rem', paddingLeft: '0.5rem' }}>
          No tasks match your filters.
        </p>
      )}
    </div>
  );

  /* ─── Render: Assignee Swimlane View ─── */
  const renderAssigneeView = () => {
    const byAssignee = new Map<string, { name: string; tasks: EventTask[] }>();

    for (const task of filteredTasks) {
      // Collect all people assigned to this task (primary + secondary)
      const people = new Set<string>();

      // Primary assignee
      if (task.assignee) {
        people.add(`${task.assignee.firstName} ${task.assignee.lastName}`);
      }

      // Additional assignees from the join table
      if (task.assignees && task.assignees.length > 0) {
        for (const a of task.assignees) {
          if (a.member) {
            people.add(`${a.member.firstName} ${a.member.lastName}`);
          }
        }
      }

      // If nobody assigned, file under "Unassigned"
      if (people.size === 0) {
        people.add('Unassigned');
      }

      // Add the task under each assigned person
      for (const name of people) {
        if (!byAssignee.has(name)) byAssignee.set(name, { name, tasks: [] });
        byAssignee.get(name)!.tasks.push(task);
      }
    }

    const entries = Array.from(byAssignee.values()).sort((a, b) => b.tasks.length - a.tasks.length);

    return (
      <div style={{ background: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '1rem 1.25rem' }}>
        {entries.map((entry) => {
          const hasOverdue = entry.tasks.some(isOverdue);
          return (
            <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0', borderBottom: `1px solid #f3f4f6` }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK, minWidth: '130px' }}>
                {hasOverdue ? '🔴' : '🟢'} {entry.name}
              </span>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', flex: 1 }}>
                {entry.tasks.map((t) => {
                  const bg = t.status === 'Done' ? '#d1fae5'
                    : t.status === 'InProgress' ? '#fef3c7'
                    : isOverdue(t) ? '#fee2e2' : '#f3f4f6';
                  const color = t.status === 'Done' ? '#065f46'
                    : t.status === 'InProgress' ? '#92400e'
                    : isOverdue(t) ? '#991b1b' : '#4b5563';
                  return (
                    <span key={t.id} style={{
                      padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem',
                      fontWeight: 500, whiteSpace: 'nowrap', background: bg, color,
                      textDecoration: t.status === 'Done' ? 'line-through' : undefined,
                    }}>
                      {t.title.length > 25 ? t.title.slice(0, 25) + '...' : t.title}
                      {isOverdue(t) && ' ⚠️'}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
        {entries.length === 0 && (
          <p style={{ color: TEXT_MUTED, fontSize: '0.85rem', padding: '1rem 0' }}>No tasks match your filters.</p>
        )}
      </div>
    );
  };

  /* ─── Render: Table View ─── */
  const renderTableView = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${BORDER_LIGHT}` }}>
            <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, color: TEXT_DARK }}>Task</th>
            <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, color: TEXT_DARK }}>Event</th>
            <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, color: TEXT_DARK }}>Priority</th>
            <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, color: TEXT_DARK }}>Due</th>
            <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, color: TEXT_DARK }}>Assignee</th>
            <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, color: TEXT_DARK }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredTasks.map((task) => {
            const overdue = isOverdue(task);
            return (
              <tr key={task.id} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                <td style={{ padding: '0.5rem', maxWidth: '280px' }}>
                  <span style={{ fontWeight: 500, ...(task.status === 'Done' ? { textDecoration: 'line-through', opacity: 0.6 } : {}) }}>
                    {task.title}
                  </span>
                </td>
                <td style={{ padding: '0.5rem', color: TEXT_MUTED }}>{task.event?.name || '—'}</td>
                <td style={{ padding: '0.5rem' }}>
                  <span style={
                    task.priority === 'High' ? { color: '#dc2626', fontWeight: 600 }
                    : task.priority === 'Medium' ? { color: '#d97706' }
                    : { color: '#059669' }
                  }>
                    {task.priority}
                  </span>
                </td>
                <td style={{ padding: '0.5rem', color: overdue ? '#ef4444' : TEXT_MUTED, fontWeight: overdue ? 600 : 400 }}>
                  {formatDate(task.dueDate)}{overdue ? ' ⚠️' : ''}
                </td>
                <td style={{ padding: '0.5rem', color: TEXT_MUTED }}>
                  {task.assignee ? `${task.assignee.firstName} ${task.assignee.lastName}` : '—'}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <button
                    onClick={() => cycleStatus(task)}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500,
                      color: task.status === 'Done' ? '#059669' : task.status === 'InProgress' ? '#d97706' : TEXT_MUTED,
                    }}
                    aria-label={`Cycle status of ${task.title}`}
                  >
                    {task.status === 'Done' ? '✅ Done' : task.status === 'InProgress' ? '🔄 Active' : '⬜ To Do'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filteredTasks.length === 0 && !loading && (
        <p style={{ color: TEXT_MUTED, fontSize: '0.85rem', padding: '1.5rem', textAlign: 'center' }}>No tasks match your filters.</p>
      )}
    </div>
  );

  /* ─── Main Render ─── */
  if (loading) {
    return (
      <div style={isMobile ? styles.pageMobile : styles.page}>
        <p style={{ color: TEXT_MUTED, textAlign: 'center', padding: '3rem 0' }}>Loading weekend tasks...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={isMobile ? styles.pageMobile : styles.page}>
        <p style={{ color: '#dc2626', textAlign: 'center', padding: '3rem 0' }}>{error}</p>
        <button onClick={fetchTasks} style={{ ...styles.qaBtn, margin: '0 auto', display: 'flex' }}>Retry</button>
      </div>
    );
  }

  return (
    <div style={isMobile ? styles.pageMobile : styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={isMobile ? { ...styles.title, fontSize: '1.3rem' } : styles.title}>📋 Weekend Tasks</h1>
        <p style={styles.subtitle}>
          {isMobile ? 'Tap checkbox to update status' : 'All tasks supporting the reunion weekend events'}
        </p>
      </div>

      {/* Progress Banner */}
      <div style={isMobile ? styles.progressBannerMobile : styles.progressBanner}>
        <ProgressRing percent={percent} size={isMobile ? 42 : 64} />
        <div>
          <div style={isMobile ? { ...styles.headline, fontSize: '0.9rem' } : styles.headline}>
            {doneTasks}/{totalTasks} complete
          </div>
          <div style={isMobile ? { ...styles.breakdown, fontSize: '0.68rem' } : styles.breakdown}>
            <span>🔄 {inProgressTasks}</span>
            <span>⬜ {todoTasks}</span>
            {overdueTasks > 0 && <span style={{ color: '#fca5a5' }}>⚠️ {overdueTasks}</span>}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActions}>
        <button style={styles.qaBtn} onClick={fetchTasks}>🔄 Refresh</button>
        {!isMobile && <button style={styles.qaBtn} onClick={handlePrint}>🖨️ Print Report</button>}
        {!isMobile && <button style={styles.qaBtn} onClick={handleSendNotification}>📤 Send Summary</button>}
      </div>

      {/* View Toggle (desktop) + Filters */}
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={styles.viewToggle}>
            <button style={viewMode === 'timeline' ? styles.viewBtnActive : styles.viewBtn} onClick={() => setViewMode('timeline')}>📅 Timeline</button>
            <button style={viewMode === 'assignee' ? styles.viewBtnActive : styles.viewBtn} onClick={() => setViewMode('assignee')}>👥 By Assignee</button>
            <button style={viewMode === 'table' ? styles.viewBtnActive : styles.viewBtn} onClick={() => setViewMode('table')}>📋 Table</button>
          </div>
        </div>
      )}

      {/* Filter Pills */}
      <div style={isMobile ? styles.filterBarMobile : styles.filterBar}>
        <span style={statusFilter === '' && !showMyOnly ? styles.filterPillActive : styles.filterPill} onClick={() => { setStatusFilter(''); setShowMyOnly(false); }}>All</span>
        <span style={showMyOnly ? styles.filterPillActive : styles.filterPill} onClick={() => setShowMyOnly(!showMyOnly)}>My Tasks</span>
        <span style={statusFilter === 'overdue' ? styles.filterPillActive : styles.filterPill} onClick={() => setStatusFilter(statusFilter === 'overdue' ? '' : 'overdue')}>⚠️ Overdue</span>
        <span style={statusFilter === 'InProgress' ? styles.filterPillActive : styles.filterPill} onClick={() => setStatusFilter(statusFilter === 'InProgress' ? '' : 'InProgress')}>🔄 Active</span>
        <span style={statusFilter === 'ToDo' ? styles.filterPillActive : styles.filterPill} onClick={() => setStatusFilter(statusFilter === 'ToDo' ? '' : 'ToDo')}>⬜ To Do</span>
        <span style={statusFilter === 'Done' ? styles.filterPillActive : styles.filterPill} onClick={() => setStatusFilter(statusFilter === 'Done' ? '' : 'Done')}>✅ Done</span>
      </div>

      {/* Content */}
      {viewMode === 'timeline' && renderTimeline()}
      {viewMode === 'assignee' && renderAssigneeView()}
      {viewMode === 'table' && renderTableView()}
    </div>
  );
}
